import { useState, useCallback, useEffect } from 'react';
import { ServiceDefinition, SearchParameters, Location } from '../models';
import { tideDataService, MonthData } from '../services/TideDataService';

interface UseMonthDataProps {
  locationId: number | null;
  services: ServiceDefinition[];
}

export function useMonthData({ locationId, services }: UseMonthDataProps) {
  // Current view date
  const [viewDate, setViewDate] = useState<Date>(() => {
    return new Date();
  });
  
  // Month data state
  const [monthData, setMonthData] = useState<MonthData | null>(null);
  
  // Loading state - UI only knows if data is loading, not why
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Error state
  const [error, setError] = useState<string | null>(null);
  
  // Current selected day
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // We're no longer auto-fetching data when locationId changes
  // This ensures search is only performed when the user explicitly clicks the search button
  useEffect(() => {
    // Only trigger automatic data fetch when month changes (navigation), not when location changes
    // Skip if we don't have required data
    if (!locationId || services.length === 0 || !monthData) {
      return; // Skip if no month data exists yet (waiting for explicit search)
    }
    
    let isMounted = true;
    
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      
      try {
        // Additional null check to satisfy TypeScript
        if (!locationId) {
          throw new Error('Location ID is required');
        }
        
        // Create a SearchParameters object to pass to tideDataService
        const searchParams: SearchParameters = {
          location: { id: locationId, name: `Location ${locationId}`, coordinates: { latitude: 0, longitude: 0 } },
          dateOfInterest: viewDate,
          services
        };
        
        const data = await tideDataService.getMonthData(searchParams);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setMonthData(data);
          
          // Set selectedDay to first available day with data
          // When changing months, we should always reset the selected day
          const availableDates = Object.keys(data.dailySchedulesByDate).sort();
          if (availableDates.length > 0) {
            setSelectedDay(availableDates[0]);
          } else {
            // If no dates available for this month, clear selected day
            setSelectedDay(null);
          }
        }
      } catch (err) {
        console.error('Error fetching month data:', err);
        if (isMounted) {
          setError('Failed to fetch tide data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }
    
    // Only fetch when month changes and we already have data (from an initial search)
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [viewDate, monthData]); // Removed locationId and services from dependencies

  // Navigate to previous month
  const goToPreviousMonth = useCallback(() => {
    setViewDate(current => {
      const newDate = new Date(current);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  }, []);

  // Navigate to next month
  const goToNextMonth = useCallback(() => {
    setViewDate(current => {
      const newDate = new Date(current);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  }, []);

  // Set view date from a string
  const setViewDateFromString = useCallback((dateString: string) => {
    // Parse the date string format YYYY-MM-DD directly to avoid timezone issues
    const [yearStr, monthStr] = dateString.split('-');
    const year = parseInt(yearStr, 10);
    // Convert from 1-indexed (01-12) to 0-indexed (0-11) for internal JavaScript use
    const month = parseInt(monthStr, 10) - 1;
    
    const newDate = new Date(year, month, 1);
    setViewDate(newDate);
  }, []);

  // Force refresh current month data
  const refreshCurrentMonth = useCallback(async () => {
    if (!locationId || services.length === 0) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Additional null check to satisfy TypeScript
      if (!locationId) {
        throw new Error('Location ID is required');
      }
      
      // Create a SearchParameters object to pass to tideDataService
      const searchParams: SearchParameters = {
        location: { id: locationId, name: `Location ${locationId}`, coordinates: { latitude: 0, longitude: 0 } },
        dateOfInterest: viewDate,
        services
      };
      
      const data = await tideDataService.getMonthData(searchParams, true); // Force refresh
      
      setMonthData(data);
    } catch (err) {
      console.error('Error refreshing month data:', err);
      setError('Failed to refresh tide data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, services, viewDate]);

  return {
    viewDate,
    monthData,
    isLoading,
    error,
    selectedDay,
    setSelectedDay,
    goToPreviousMonth,
    goToNextMonth,
    setViewDate: setViewDateFromString,
    refreshCurrentMonth
  };
}
